import React from 'react';

const EDGE_EPSILON = 2;

export default function useAutoBounceScroll({
  enabled = true,
  pageStepRatio = 0.9,
  durationDownMs = 2000,
  durationUpMs = 1000,
  pauseMs = 8000,
  pauseTopMs = pauseMs,
  pauseBottomMs = pauseMs
} = {}) {
  const containerRef = React.useRef(null);
  // Reattach effects when async content mounts or the layout replaces its node.
  const [element, setElement] = React.useState(null);
  const attachContainer = React.useCallback((node) => {
    containerRef.current = node;
    setElement(node);
  }, []);
  const directionRef = React.useRef(1);
  const timeoutRef = React.useRef(null);
  const animationFrameRef = React.useRef(null);
  const measureFrameRef = React.useRef(null);
  const isAnimatingRef = React.useRef(false);
  const [overflowState, setOverflowState] = React.useState({
    hasOverflow: false,
    canScrollUp: false,
    canScrollDown: false,
    scrollDirection: 'down'
  });

  const updateOverflowState = React.useCallback(() => {
    const element = containerRef.current;

    if (!element) {
      return;
    }

    const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight);
    const hasOverflow = maxScrollTop > EDGE_EPSILON;
    const nextState = {
      hasOverflow,
      canScrollUp: hasOverflow && element.scrollTop > EDGE_EPSILON,
      canScrollDown: hasOverflow && element.scrollTop < maxScrollTop - EDGE_EPSILON,
      scrollDirection: directionRef.current > 0 ? 'down' : 'up'
    };

    setOverflowState((prevState) =>
      prevState.hasOverflow === nextState.hasOverflow &&
      prevState.canScrollUp === nextState.canScrollUp &&
      prevState.canScrollDown === nextState.canScrollDown &&
      prevState.scrollDirection === nextState.scrollDirection
        ? prevState
        : nextState
    );
  }, []);

  const resetScroll = React.useCallback(() => {
    const element = containerRef.current;

    if (!element) {
      return;
    }

    element.scrollTop = 0;
    directionRef.current = 1;
    updateOverflowState();
  }, [updateOverflowState]);

  React.useEffect(() => {
    if (!element) {
      return undefined;
    }

    const measure = () => {
      if (measureFrameRef.current !== null || isAnimatingRef.current) {
        return;
      }

      const raf = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
      measureFrameRef.current = raf(() => {
        measureFrameRef.current = null;
        updateOverflowState();
      });
    };

    updateOverflowState();

    const onScroll = () => measure();
    element.addEventListener('scroll', onScroll, { passive: true });

    window.addEventListener('resize', measure);

    const resizeObserver =
      typeof window.ResizeObserver !== 'undefined'
        ? new window.ResizeObserver(() => {
            measure();
          })
        : null;

    if (resizeObserver) {
      resizeObserver.observe(element);
      if (element.parentElement) {
        resizeObserver.observe(element.parentElement);
      }
    }

    const mutationObserver =
      typeof window.MutationObserver !== 'undefined'
        ? new window.MutationObserver(() => {
            measure();
          })
        : null;

    if (mutationObserver) {
      mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });
    }

    return () => {
      element.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      if (measureFrameRef.current !== null) {
        (window.cancelAnimationFrame || window.clearTimeout)(measureFrameRef.current);
        measureFrameRef.current = null;
      }
    };
  }, [element, updateOverflowState]);

  React.useEffect(() => {
    const prefersReducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!enabled || !overflowState.hasOverflow || prefersReducedMotion) {
      return undefined;
    }

    let isCancelled = false;

    const animateTo = (element, target, durationMs, onDone) => {
      const start = element.scrollTop;
      const delta = target - start;

      if (Math.abs(delta) <= EDGE_EPSILON) {
        onDone();
        return;
      }

      const startedAt = window.performance.now();
      const effectiveDuration = Math.max(80, durationMs);
      const easeInOut = (value) =>
        value < 0.5 ? 2 * value * value : 1 - Math.pow(-2 * value + 2, 2) / 2;

      const tick = (now) => {
        if (isCancelled) {
          return;
        }

        if (document.hidden) {
          isAnimatingRef.current = false;
          timeoutRef.current = window.setTimeout(runStep, pauseTopMs);
          return;
        }

        const progress = Math.min(1, (now - startedAt) / effectiveDuration);
        element.scrollTop = start + delta * easeInOut(progress);

        if (progress < 1) {
          animationFrameRef.current = window.requestAnimationFrame(tick);
          return;
        }

        animationFrameRef.current = null;
        isAnimatingRef.current = false;
        updateOverflowState();
        onDone();
      };

      isAnimatingRef.current = true;
      animationFrameRef.current = window.requestAnimationFrame(tick);
    };

    const runStep = () => {
      if (isCancelled) {
        return;
      }

      const element = containerRef.current;
      if (!element) {
        return;
      }

      const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight);
      if (maxScrollTop <= EDGE_EPSILON) {
        updateOverflowState();
        return;
      }

      const pageStep = Math.max(24, Math.floor(element.clientHeight * pageStepRatio));
      const movingDown = directionRef.current > 0;

      if (movingDown) {
        const target = Math.min(maxScrollTop, element.scrollTop + pageStep);
        const reachesBottom = target >= maxScrollTop - EDGE_EPSILON;

        animateTo(element, target, durationDownMs, () => {
          if (isCancelled) {
            return;
          }
          if (reachesBottom) {
            directionRef.current = -1;
          }
          updateOverflowState();
          timeoutRef.current = window.setTimeout(runStep, pauseBottomMs);
        });
        return;
      }

      animateTo(element, 0, durationUpMs, () => {
        if (isCancelled) {
          return;
        }
        directionRef.current = 1;
        updateOverflowState();
        timeoutRef.current = window.setTimeout(runStep, pauseTopMs);
      });
    };

    timeoutRef.current = window.setTimeout(runStep, pauseTopMs);

    return () => {
      isCancelled = true;
      isAnimatingRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [
    durationDownMs,
    durationUpMs,
    enabled,
    element,
    overflowState.hasOverflow,
    pageStepRatio,
    pauseBottomMs,
    pauseTopMs,
    updateOverflowState
  ]);

  return {
    containerRef: attachContainer,
    resetScroll,
    ...overflowState
  };
}
